// Extend Window interface to include gtag
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

/**
 * Track custom events in Google Analytics
 * @param eventName - Name of the event (e.g., 'button_click', 'form_submit')
 * @param eventParams - Additional parameters for the event
 */
export function trackEvent(eventName: string, eventParams: Record<string, any> = {}) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
}

/**
 * Track conversion events (for ads campaigns)
 * @param conversionId - Google Ads conversion ID
 * @param conversionLabel - Conversion label from Google Ads
 * @param value - Optional conversion value
 */
export function trackConversion(
  conversionId: string, 
  conversionLabel: string, 
  value?: number
) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'conversion', {
      send_to: `${conversionId}/${conversionLabel}`,
      value: value,
      currency: 'USD',
    });
  }
}

/**
 * Enhanced event tracking with Supabase integration
 * Tracks events to both Google Analytics and Supabase database
 */
export interface EngagementEventData {
  eventName: string;
  eventCategory: 'subscription' | 'payment' | 'request' | 'portal' | 'auth' | 'file' | 'communication' | 'dashboard';
  eventData?: Record<string, any>;
  conversionValue?: number;
  conversionCurrency?: string;
  subscriptionId?: string;
  planId?: string;
  requestId?: string;
}

/**
 * Track engagement event to both GA and Supabase
 */
export async function trackEngagementEvent(data: EngagementEventData) {
  // Track to Google Analytics
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', data.eventName, {
      event_category: data.eventCategory,
      value: data.conversionValue,
      currency: data.conversionCurrency || 'USD',
      ...data.eventData
    });
  }
  
  // Track to Supabase
  try {
    const response = await fetch('/api/analytics/track-event', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        ...data,
        pagePath: window.location.pathname,
        referrer: document.referrer,
        userAgent: navigator.userAgent,
        sessionId: getOrCreateSessionId()
      })
    });
    
    if (!response.ok) {
      console.error('Failed to track event to Supabase:', await response.text());
    }
  } catch (error) {
    console.error('Error tracking event to Supabase:', error);
  }
}

/**
 * Track subscription tier selection
 */
export function trackSubscriptionTierSelection(planName: string, planId: string, price: number) {
  trackEngagementEvent({
    eventName: 'subscription_tier_selected',
    eventCategory: 'subscription',
    eventData: {
      plan_name: planName,
      plan_id: planId,
      price: price
    },
    planId: planId
  });
}

/**
 * Track payment completion
 */
export function trackPaymentCompletion(
  subscriptionId: string,
  planId: string,
  amount: number,
  currency: string = 'USD'
) {
  trackEngagementEvent({
    eventName: 'payment_completed',
    eventCategory: 'payment',
    conversionValue: amount,
    conversionCurrency: currency,
    subscriptionId: subscriptionId,
    planId: planId,
    eventData: {
      payment_method: 'stripe',
      subscription_id: subscriptionId
    }
  });
  
  // Track conversion in Google Analytics
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'purchase', {
      transaction_id: subscriptionId,
      value: amount,
      currency: currency,
      items: [{
        item_id: planId,
        item_name: 'Subscription',
        price: amount,
        quantity: 1
      }]
    });
  }
}

/**
 * Track payment failure
 */
export function trackPaymentFailure(
  planId: string,
  amount: number,
  errorMessage: string
) {
  trackEngagementEvent({
    eventName: 'payment_failed',
    eventCategory: 'payment',
    planId: planId,
    eventData: {
      error_message: errorMessage,
      amount: amount
    }
  });
}

/**
 * Track request submission
 */
export function trackRequestSubmission(
  requestId: string,
  requestType: string,
  priority: string
) {
  trackEngagementEvent({
    eventName: 'request_submitted',
    eventCategory: 'request',
    requestId: requestId,
    eventData: {
      request_type: requestType,
      priority: priority
    }
  });
}

/**
 * Track portal feature usage
 */
export function trackPortalFeatureUsage(
  featureName: string,
  action: string,
  metadata?: Record<string, any>
) {
  trackEngagementEvent({
    eventName: 'portal_feature_used',
    eventCategory: 'portal',
    eventData: {
      feature_name: featureName,
      action: action,
      ...metadata
    }
  });
}

/**
 * Track user journey stage transition
 */
export async function trackJourneyStage(
  stage: 'awareness' | 'consideration' | 'signup' | 'activation' | 'retention' | 'revenue' | 'referral' | 'churn',
  converted: boolean = false,
  conversionValue?: number
) {
  try {
    await fetch('/api/analytics/track-journey', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        stage,
        converted,
        conversionValue,
        sessionId: getOrCreateSessionId()
      })
    });
  } catch (error) {
    console.error('Error tracking journey stage:', error);
  }
}

/**
 * Track discovery call button clicks across all pages
 */
export async function trackDiscoveryCallClick(
  userSegment: 'unauthenticated' | 'client' | 'team' | 'admin' | 'super_admin',
  pageLocation: string,
  buttonLocation: string
) {
  // Track to Google Analytics
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'discovery_call_click', {
      user_segment: userSegment,
      page_location: pageLocation,
      button_location: buttonLocation,
    });
  }

  // Track to Supabase with funnel integration
  try {
    let sessionId = getOrCreateSessionId();
    
    const response = await fetch('/api/analytics/track-discovery-click', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userSegment,
        pageLocation,
        buttonLocation,
        sessionId,
      }),
    });

    if (!response.ok) {
      console.error('Failed to track discovery call click:', await response.text());
    }
  } catch (error) {
    console.error('Error tracking discovery call click:', error);
  }
}

/**
 * Track Calendly widget view
 */
export async function trackCalendlyView(sessionId?: string) {
  const sid = sessionId || getOrCreateSessionId();
  
  // Track to Google Analytics
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'calendly_view', {
      session_id: sid,
    });
  }

  // Track to Supabase funnel
  try {
    const response = await fetch('/api/analytics/track-calendly-view', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ sessionId: sid }),
    });

    if (!response.ok) {
      console.error('Failed to track Calendly view:', await response.text());
    }
  } catch (error) {
    console.error('Error tracking Calendly view:', error);
  }
}

/**
 * Get or create session ID for journey tracking
 */
function getOrCreateSessionId(): string {
  if (typeof window === 'undefined') return '';
  
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
}

/**
 * Track subscription cancellation
 */
export function trackSubscriptionCancellation(
  subscriptionId: string,
  planId: string,
  reason?: string
) {
  trackEngagementEvent({
    eventName: 'subscription_cancelled',
    eventCategory: 'subscription',
    subscriptionId: subscriptionId,
    planId: planId,
    eventData: {
      cancellation_reason: reason
    }
  });
  
  // Track journey stage to churn
  trackJourneyStage('churn');
}

/**
 * Track user signup
 */
export function trackUserSignup(userId: string) {
  trackEngagementEvent({
    eventName: 'user_signup',
    eventCategory: 'auth',
    eventData: {
      user_id: userId
    }
  });
  
  // Track journey stage to signup
  trackJourneyStage('signup');
}

/**
 * Track user login
 */
export function trackUserLogin(userId: string) {
  trackEngagementEvent({
    eventName: 'user_login',
    eventCategory: 'auth',
    eventData: {
      user_id: userId
    }
  });
}

/**
 * No-op engagement tracking hook (page-view tracking handled by GoogleAnalytics component)
 */
export function useEngagementTracking() {
  // Page view tracking is handled by useGoogleAnalytics hook in GoogleAnalytics.tsx
}